import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import { useTeam } from '../../../context/TeamContext';
import { useEquipment } from '../../../context/EquipmentContext';
import { useShift } from '../../../context/ShiftContext';
import { useNotification } from '../../../context/NotificationContext';
import { INSPECTION_ROUNDS } from '../../../data/nurseAppModels';

const InspectionReminderWidget = ({ compact = false }) => {
  const { currentUser } = useAuth();
  const { currentTeam } = useTeam();
  const { getPendingInspections, equipment } = useEquipment();
  const { getTodayShifts } = useShift();
  const { getEquipmentNotificationCount, getEquipmentPendingActionsCount, notifications } = useNotification();
  const location = useLocation();
  const [pendingInspections, setPendingInspections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notificationCount, setNotificationCount] = useState(0);
  const [pendingActionsCount, setPendingActionsCount] = useState(0);

  useEffect(() => {
    const loadPendingInspections = async () => {
      if (!currentUser || !currentTeam) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const pending = await getPendingInspections();
        setPendingInspections(pending);
      } catch (err) {
        console.error('Error loading pending inspections:', err);
      } finally {
        setLoading(false);
      }
    };

    loadPendingInspections();
  }, [currentUser, currentTeam, getPendingInspections, equipment]);

  // Load equipment notification count and pending actions count
  useEffect(() => {
    if (!currentTeam || !getEquipmentNotificationCount || !currentUser) {
      setNotificationCount(0);
      setPendingActionsCount(0);
      return;
    }
    
    const userRole = currentTeam.userRole || 'MEMBER';
    const count = getEquipmentNotificationCount(userRole);
    console.log('🔔 InspectionReminderWidget: Notification count:', {
      userRole,
      count,
      totalNotifications: notifications?.length || 0,
      unreadNotifications: notifications?.filter(n => !n.read)?.length || 0
    });
    setNotificationCount(count);

    // Load pending actions count (to-do list)
    const loadPendingActions = async () => {
      if (!getEquipmentPendingActionsCount) return;
      try {
        const actionsCount = await getEquipmentPendingActionsCount(
          userRole,
          currentTeam.id,
          currentUser.uid
        );
        console.log('📋 InspectionReminderWidget: Pending actions count:', {
          userRole,
          actionsCount
        });
        setPendingActionsCount(actionsCount);
      } catch (err) {
        console.error('Error loading pending actions count:', err);
        setPendingActionsCount(0);
      }
    };

    loadPendingActions();
  }, [currentTeam, currentUser, getEquipmentNotificationCount, getEquipmentPendingActionsCount, notifications]);

  const getRoundLabel = (round) => {
    const rounds = {
      [INSPECTION_ROUNDS.MORNING_BEFORE]: 'เช้า - ก่อนเข้าเวร',
      [INSPECTION_ROUNDS.MORNING_AFTER]: 'เช้า - หลังเข้าเวร',
      [INSPECTION_ROUNDS.AFTERNOON_BEFORE]: 'บ่าย - ก่อนเข้าเวร',
      [INSPECTION_ROUNDS.AFTERNOON_AFTER]: 'บ่าย - หลังเข้าเวร',
      [INSPECTION_ROUNDS.NIGHT_BEFORE]: 'ดึก - ก่อนเข้าเวร',
      [INSPECTION_ROUNDS.NIGHT_AFTER]: 'ดึก - หลังเข้าเวร'
    };
    return rounds[round] || round;
  };

  const getRoundIcon = (round) => {
    if (round.includes('MORNING')) return '🌅';
    if (round.includes('AFTERNOON')) return '☀️';
    if (round.includes('NIGHT')) return '🌙';
    return '📋';
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'COMPLETED':
        return 'bg-green-100 text-green-800 border-green-300';
      case 'IN_PROGRESS':
        return 'bg-blue-100 text-blue-800 border-blue-300';
      case 'OVERDUE':
        return 'bg-red-100 text-red-800 border-red-300';
      case 'NOT_STARTED':
        return 'bg-gray-100 text-gray-800 border-gray-300';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-300';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'COMPLETED':
        return '✅ เสร็จแล้ว';
      case 'IN_PROGRESS':
        return '🔄 กำลังดำเนินการ';
      case 'OVERDUE':
        return '⚠️ เกินเวลา';
      case 'NOT_STARTED':
        return '⏳ ยังไม่เริ่ม';
      default:
        return '⏳ ยังไม่เริ่ม';
    }
  };

  const formatTime = (date) => {
    if (!date) return '-';
    const d = date instanceof Date ? date : date.toDate ? date.toDate() : new Date(date);
    return d.toLocaleTimeString('th-TH', { hour: '2-digit', minute: '2-digit' });
  };

  const formatDate = (date) => {
    if (!date) return '-';
    const d = date instanceof Date ? date : date.toDate ? date.toDate() : new Date(date);
    return d.toLocaleDateString('th-TH', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // Filter pending inspections
  const activeInspections = pendingInspections.filter(
    insp => insp.status !== 'COMPLETED' && insp.isTimeForRound
  );
  const upcomingInspections = pendingInspections.filter(
    insp => insp.status !== 'COMPLETED' && !insp.isTimeForRound
  );

  const totalPending = activeInspections.length + upcomingInspections.length;
  
  // Check if we're on the equipment page
  const isOnEquipmentPage = location.pathname === '/nurse/equipment';

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-md p-4">
        <div className="animate-pulse">
          <div className="h-4 bg-gray-200 rounded w-1/2 mb-2"></div>
          <div className="h-4 bg-gray-200 rounded w-3/4"></div>
        </div>
      </div>
    );
  }

  if (!currentTeam) {
    return null;
  }

  // Hide widget when on equipment page and no pending inspections (non-compact mode only)
  if (isOnEquipmentPage && totalPending === 0 && !compact) {
    return null;
  }

  if (compact) {
    // Calculate overall progress
    const totalItems = pendingInspections.reduce((sum, insp) => sum + insp.totalItems, 0);
    const totalCompleted = pendingInspections.reduce((sum, insp) => sum + insp.completedItems, 0);
    const overallProgress = totalItems > 0 ? Math.round((totalCompleted / totalItems) * 100) : 0;
    
    // Get active inspection status
    const activeInspection = activeInspections.length > 0 ? activeInspections[0] : null;
    const hasInProgress = activeInspections.some(insp => insp.status === 'IN_PROGRESS');
    const hasOverdue = activeInspections.some(insp => insp.status === 'OVERDUE');

    return (
      <Link
        to="/nurse/equipment"
        className="block h-full relative flex flex-col"
      >
        {/* Notification Badge - Show pending actions count (to-do list) */}
        {(pendingActionsCount > 0 || notificationCount > 0) && (
          <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-bold rounded-full h-6 w-6 flex items-center justify-center z-20 shadow-lg animate-pulse">
            {(pendingActionsCount + notificationCount) > 9 ? '9+' : (pendingActionsCount + notificationCount)}
          </span>
        )}
        
        <div className="flex flex-col items-center justify-center flex-1 relative">
          {/* Icon */}
          <div className="text-4xl mb-2">🩺</div>
          
          {/* Title */}
          <h3 className="font-semibold text-gray-800 mb-1 text-center">อุปกรณ์</h3>
          
          {/* Status Row 1: Main Status */}
          {hasOverdue ? (
            <p className="text-xs text-red-600 font-semibold text-center mb-1">
              ⚠️ เกินเวลา {totalPending} รายการ
            </p>
          ) : hasInProgress ? (
            <p className="text-xs text-blue-600 font-semibold text-center mb-1">
              🔄 กำลังตรวจสอบ {totalPending} รายการ
            </p>
          ) : totalPending > 0 ? (
            <p className="text-xs text-orange-600 font-semibold text-center mb-1">
              ⏰ รอตรวจสอบ {totalPending} รายการ
            </p>
          ) : (
            <p className="text-xs text-gray-600 text-center mb-1">พร้อมใช้งาน</p>
          )}
          
          {/* Status Row 2: Progress (if has active inspection) */}
          {activeInspection && activeInspection.status === 'IN_PROGRESS' && (
            <div className="w-full px-2 mb-1">
              <div className="flex items-center justify-between text-xs mb-0.5">
                <span className="text-gray-600">
                  {activeInspection.completedItems}/{activeInspection.totalItems} รายการ
                </span>
                <span className="text-gray-600">{activeInspection.progressPercent}%</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-1.5">
                <div
                  className="bg-blue-600 h-1.5 rounded-full transition-all"
                  style={{ width: `${activeInspection.progressPercent}%` }}
                ></div>
              </div>
            </div>
          )}
          
          {/* Status Row 3: Next Round Info */}
          {activeInspection && (
            <p className="text-xs text-gray-500 text-center mt-1">
              {getRoundLabel(activeInspection.round)}
            </p>
          )}
          
          {/* Team Badge - Bottom */}
          {currentTeam && (
            <div className="mt-auto pt-2 w-full">
              <p className="text-xs text-gray-400 text-center truncate">
                👥 {currentTeam.name}
              </p>
            </div>
          )}
        </div>
      </Link>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <div className="text-2xl">🩺</div>
          <div>
            <h2 className="text-lg font-bold text-gray-800">อุปกรณ์</h2>
            {currentTeam && (
              <span className="text-xs text-blue-600 font-medium">
                👥 ทีม: {currentTeam.name}
              </span>
            )}
          </div>
        </div>
        {totalPending > 0 && (
          <span className="px-3 py-1 bg-red-100 text-red-800 rounded-full text-sm font-semibold">
            ⚠️ {totalPending} รายการต้องตรวจสอบ
          </span>
        )}
      </div>

      {totalPending === 0 ? (
        <div className="text-center py-4">
          <p className="text-gray-600">✅ ไม่มีรายการที่ต้องตรวจสอบ</p>
          <p className="text-sm text-gray-500 mt-2">ทุกอย่างพร้อมใช้งาน</p>
          <Link
            to="/nurse/equipment/inspection"
            className="mt-4 inline-block px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium"
          >
            เริ่มตรวจสอบอุปกรณ์
          </Link>
        </div>
      ) : (
        <div className="space-y-4">
          {/* Active Inspections */}
          {activeInspections.length > 0 && (
            <div>
              <h3 className="text-sm font-semibold text-gray-700 mb-2">📅 วันนี้ ({formatDate(new Date())})</h3>
              {activeInspections.map((inspection, index) => (
                <div
                  key={index}
                  className={`border rounded-lg p-4 mb-3 ${getStatusColor(inspection.status)}`}
                >
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <span className="text-xl">{getRoundIcon(inspection.round)}</span>
                      <div>
                        <h4 className="font-semibold">{getRoundLabel(inspection.round)}</h4>
                        <p className="text-xs">
                          ⏰ {formatTime(inspection.shiftTime.start)} - {formatTime(inspection.shiftTime.end)}
                        </p>
                      </div>
                    </div>
                    <span className="text-xs font-semibold">{getStatusLabel(inspection.status)}</span>
                  </div>
                  
                  {inspection.status === 'IN_PROGRESS' && (
                    <div className="mt-2">
                      <div className="flex items-center justify-between text-xs mb-1">
                        <span>📊 ตรวจสอบแล้ว: {inspection.completedItems}/{inspection.totalItems} รายการ</span>
                        <span>{inspection.progressPercent}%</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div
                          className="bg-blue-600 h-2 rounded-full transition-all"
                          style={{ width: `${inspection.progressPercent}%` }}
                        ></div>
                      </div>
                    </div>
                  )}

                  <Link
                    to={inspection.inspectionId 
                      ? `/nurse/equipment/inspection/${inspection.inspectionId}`
                      : `/nurse/equipment/inspection?round=${inspection.round}`
                    }
                    className="mt-3 inline-block w-full text-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium"
                  >
                    {inspection.status === 'IN_PROGRESS' ? '🔍 ตรวจสอบต่อ' : '🔍 เริ่มตรวจสอบ'}
                  </Link>
                </div>
              ))}
            </div>
          )}

          {/* Upcoming Inspections */}
          {upcomingInspections.length > 0 && (
            <div>
              <h3 className="text-sm font-semibold text-gray-700 mb-2">⏸️ ยังไม่ถึงเวลา</h3>
              {upcomingInspections.map((inspection, index) => (
                <div
                  key={index}
                  className="border border-gray-200 rounded-lg p-3 mb-2 bg-gray-50"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="text-lg">{getRoundIcon(inspection.round)}</span>
                      <div>
                        <h4 className="text-sm font-medium text-gray-700">{getRoundLabel(inspection.round)}</h4>
                        <p className="text-xs text-gray-500">
                          ⏰ {formatTime(inspection.shiftTime.start)} - {formatTime(inspection.shiftTime.end)}
                        </p>
                      </div>
                    </div>
                    <span className="text-xs text-gray-500">⏸️ ยังไม่ถึงเวลา</span>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Action Button */}
          <Link
            to="/nurse/equipment/inspection"
            className="block w-full text-center px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-semibold mt-4"
          >
            🔍 ตรวจสอบอุปกรณ์
          </Link>
        </div>
      )}
    </div>
  );
};

export default InspectionReminderWidget;

